import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Slider,
  Paper,
  Box,
  Divider,
  Checkbox,
  Avatar,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormControl
} from '@mui/material';
import Typography from '@mui/material/Typography';
import { Button, TextField } from '@mui/material';
import { useHistory } from 'react-router-dom';
import {
  AdminGetCarriers,
  CreateOrUpdateAgency,
  UploadAgencyLogo
} from '../../apis/Admin';
import { ErrorToast, successToast } from '../../components/Toasts';
import { ToastContainer } from 'react-toastify';
import NoLogo from '../../assets/carrier logos/No Logo.png';
import { base_logo_url } from '../../apis/constants';
import Cropper from 'react-easy-crop';
import getCroppedImg from './cropImage';
import AddressAutocomplete from '../AddressAutoComplete';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';

// Compact Carrier Card Component
const CarrierCard = ({ carrier, isSelected, onToggle }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: 120,
      height: 70,
      p: 0.75,
      borderRadius: 2,
      border: '1px solid',
      borderColor: isSelected ? 'primary.main' : '#e0e0e0',
      bgcolor: isSelected ? '#e3f2fd' : 'background.paper',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      position: 'relative',
      '&:hover': {
        borderColor: 'primary.main',
        bgcolor: isSelected ? '#e3f2fd' : '#f5f5f5',
        transform: 'translateY(-1px)',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }
    }}
    onClick={() => onToggle(carrier.id)}
  >
    <Checkbox
      checked={isSelected}
      onChange={e => {
        e.stopPropagation();
        onToggle(carrier.id);
      }}
      onClick={e => {
        e.stopPropagation();
      }}
      size='small'
      sx={{
        position: 'absolute',
        top: 2,
        right: 2,
        p: 0.25,
        '& .MuiSvgIcon-root': { fontSize: 11, borderRadius: '50%' }
      }}
    />
    <Avatar
      variant='rounded'
      src={carrier.icon ? base_logo_url + carrier.icon : NoLogo}
      sx={{ width: 44, height: 44, mb: 0.5, objectFit: 'cover' }}
    >
      {!carrier.icon && carrier.name?.charAt(0)}
    </Avatar>
    <Typography
      variant='caption'
      sx={{
        fontSize: '0.65rem',
        fontWeight: 500,
        textAlign: 'center',
        whiteSpace: 'nowrap',
        lineHeight: 1.2,
        maxWidth: 64
      }}
    >
      {carrier.name}
    </Typography>
  </Box>
);

export default function CreateAgency(props) {
  const EditedData = props?.location?.state;
  const history = useHistory();
  const [createAgencyLoader, setCreateAgencyLoader] = useState(false);
  const [carriersLoader, setCarriersLoader] = useState(true);
  const [carrierTab, setCarrierTab] = useState('All');

  // Agency states
  const [agencyStatus, setAgencyStatus] = useState(
    EditedData?.active ? 'Active' : 'Inactive'
  );
  const [companyName, setCompanyName] = useState(EditedData?.company || '');
  const [agencyImage, setAgencyImage] = useState(
    EditedData?.logo ? base_logo_url + EditedData?.logo : null
  );
  const [agencyApiUploadedImage, setAgencyApiUploadedImage] = useState(
    EditedData?.logo || ''
  );

  // Carriers
  const [allCarriers, setAllCarriers] = useState([]);
  const [selectedCarrierIds, setSelectedCarrierIds] = useState(new Set());

  // Admin
  const [firstName, setFirstName] = useState(
    EditedData?.adminInfo?.firstName || ''
  );
  const [lastName, setLastName] = useState(
    EditedData?.adminInfo?.lastName || ''
  );
  const [email, setEmail] = useState(EditedData?.adminInfo?.email || '');
  const [phoneNumber, setPhoneNumber] = useState(
    EditedData?.adminInfo?.phone || ''
  );

  // Individual field validation errors
  const [fieldError, setFieldError] = useState({
    companyName: '',
    address: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });

  // Address
  const [address, setAddress] = useState(
    EditedData
      ? {
          City: EditedData?.city || '',
          State: EditedData?.state || '',
          ZipCode: EditedData?.zipCode || '',
          CountryCode: EditedData?.country || '',
          address: EditedData?.address1 || '',
          isManualEntry: false
        }
      : {
          City: '',
          State: '',
          ZipCode: '',
          CountryCode: '',
          address: '',
          isManualEntry: false
        }
  );
  const addressRef = useRef(address);
  // Image crop
  const [dialogOpen, setDialogOpen] = useState(false);
  const [tempImage, setTempImage] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  // Validation functions
  const validateCompanyName = useCallback(value => {
    if (!value) return 'Agency name is required';
    return '';
  }, []);

  const validateAddressComplete = useCallback(() => {
    const current = addressRef.current;
    const hasAddress = !!(current?.address && current.address.trim());
    if (!hasAddress) return 'Address is required';
    return '';
  }, []);

  const validateFirstName = useCallback(value => {
    if (!value) return 'First name is required';
    if (!/^[a-zA-Z]+$/.test(value)) return 'Only letters allowed';
    return '';
  }, []);

  const validateLastName = useCallback(value => {
    if (!value) return 'Last name is required';
    if (!/^[a-zA-Z]+$/.test(value)) return 'Only letters allowed';
    return '';
  }, []);

  const validateEmail = useCallback(value => {
    if (!value) return 'Email is required';
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(value)) return 'Enter a valid email address';
    return '';
  }, []);

  const validatePhone = useCallback(value => {
    if (!value) return 'Phone number is required';
    const digits = value.replace(/\D/g, '');
    if (digits.length !== 10) return 'Phone number must be 10 digits';
    return '';
  }, []);

  // Handle field blur
  const handleFieldBlur = (fieldName, value, validateFn) => {
    const error = validateFn(value);
    setFieldError(prev => ({ ...prev, [fieldName]: error }));
  };

  // Handle input change
  const handleFieldChange = (fieldName, value) => {
    if (fieldName === 'companyName') setCompanyName(value);
    if (fieldName === 'firstName') setFirstName(value);
    if (fieldName === 'lastName') setLastName(value);
    if (fieldName === 'email') setEmail(value);

    if (fieldError[fieldName]) {
      setFieldError(prev => ({ ...prev, [fieldName]: '' }));
    }
  };

  // Filtered carriers
  const filteredCarriers = useMemo(() => {
    if (!allCarriers.length) return [];
    if (carrierTab === 'All') return allCarriers;
    return allCarriers.filter(c => c.cType === carrierTab);
  }, [allCarriers, carrierTab]);

  const allSelected = useMemo(() => {
    if (!filteredCarriers.length) return false;
    return filteredCarriers.every(c => selectedCarrierIds.has(c.id));
  }, [filteredCarriers, selectedCarrierIds]);

  // Handlers
  const handleCarrierToggle = useCallback(id => {
    setSelectedCarrierIds(prev => {
      const newSet = new Set(prev);
      newSet.has(id) ? newSet.delete(id) : newSet.add(id);
      return newSet;
    });
  }, []);

  const handleSelectAll = useCallback(
    e => {
      if (e.target.checked) {
        setSelectedCarrierIds(new Set(filteredCarriers.map(c => c.id)));
      } else {
        setSelectedCarrierIds(prev => {
          const newSet = new Set(prev);
          filteredCarriers.forEach(c => newSet.delete(c.id));
          return newSet;
        });
      }
    },
    [filteredCarriers]
  );

  const handlePhoneChange = e => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 10);
    const formatted = val.replace(/^(\d{3})(\d{3})(\d{4})$/, '($1) $2-$3');
    setPhoneNumber(formatted);
    if (fieldError.phone) {
      setFieldError(prev => ({ ...prev, phone: '' }));
    }
  };

  const handlePhoneBlur = () => {
    const error = validatePhone(phoneNumber);
    setFieldError(prev => ({ ...prev, phone: error }));
  };

  const handleImageUpload = event => {
    const file = event.target.files[0];
    if (file && file.type.match(/image.*/)) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setTempImage(reader.result);
        setDialogOpen(true);
      };
      reader.readAsDataURL(file);
    } else if (file) {
      ErrorToast('Only image files are allowed');
    }
    event.target.value = null;
  };

  const handleCropConfirm = async () => {
    try {
      const croppedImage = await getCroppedImg(tempImage, croppedAreaPixels);
      setAgencyImage(croppedImage);

      const file = dataURLtoFile(croppedImage, `agency_logo_${Date.now()}.png`);
      const formData = new FormData();
      formData.append('file', file);
      const response = await UploadAgencyLogo(formData);
      if (response.status === 200) {
        setAgencyApiUploadedImage(response.data.data);
        successToast('Logo uploaded successfully');
      }
      setDialogOpen(false);
    } catch (error) {
      console.error('Crop error:', error);
      ErrorToast('Failed to crop/upload image');
    }
  };

  const dataURLtoFile = (dataUrl, fileName) => {
    const arr = dataUrl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    return new File([u8arr], fileName, { type: mime });
  };

  const validateFormSequentially = () => {
    setFieldError({
      companyName: '',
      address: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: ''
    });

    const firstNameError = validateFirstName(firstName);
    if (firstNameError) {
      setFieldError(prev => ({ ...prev, firstName: firstNameError }));
      document.getElementById('field-firstName')?.focus();
      return false;
    }

    const lastNameError = validateLastName(lastName);
    if (lastNameError) {
      setFieldError(prev => ({ ...prev, lastName: lastNameError }));
      document.getElementById('field-lastName')?.focus();
      return false;
    }

    const emailError = validateEmail(email);
    if (emailError) {
      setFieldError(prev => ({ ...prev, email: emailError }));
      document.getElementById('field-email')?.focus();
      return false;
    }

    const phoneError = validatePhone(phoneNumber);
    if (phoneError) {
      setFieldError(prev => ({ ...prev, phone: phoneError }));
      document.getElementById('field-phone')?.focus();
      return false;
    }

    const companyError = validateCompanyName(companyName);
    if (companyError) {
      setFieldError(prev => ({ ...prev, companyName: companyError }));
      document.getElementById('field-companyName')?.focus();
      return false;
    }

    const addressError = validateAddressComplete(); // no argument needed anymore
    if (addressError) {
      setFieldError(prev => ({ ...prev, address: addressError }));
      return false;
    }

    return true;
  };

  const submitForm = () => {
    setCreateAgencyLoader(true);

    const selectedCarriers = Array.from(selectedCarrierIds).map(id => ({
      carrierId: id,
      assigned: true
    }));

    const payload = {
      id: EditedData?.id || 0,
      name: companyName,
      logo: agencyApiUploadedImage,
      url: '',
      active: agencyStatus === 'Active',
      address: address.address || '',
      address2: '',
      zipCode: address.ZipCode || '',
      city: address.City || '',
      state: address.State || '',
      country: address.CountryCode || 'USA',
      subscriptioPlanId: 1,
      carriers: selectedCarriers,
      firstName: firstName,
      lastName: lastName,
      email: email,
      phone: phoneNumber ?? ''
    };

    console.log('Submitting payload:', payload);

    CreateOrUpdateAgency(payload)
      .then(res => {
        if (res.status === 200) {
          successToast(
            EditedData
              ? 'Agency Updated Successfully'
              : 'Agency Created Successfully'
          );
          setTimeout(() => {
            history.push('/manage-agencies');
          }, 2000);
        } else {
          ErrorToast(res?.data?.message);
        }
      })
      .catch(err => {
        console.error('Save error:', err);
        ErrorToast(err?.response?.data || 'Failed to save agency');
      })
      .finally(() => {
        setCreateAgencyLoader(false);
      });
  };
  const handleSubmit = () => {
    const isValid = validateFormSequentially();
    if (isValid) {
      submitForm();
    }
  };

  // Load carriers
  useEffect(() => {
    AdminGetCarriers(3)
      .then(res => {
        const carriers =
          res?.data?.data?.sort((a, b) => a.name.localeCompare(b.name)) || [];
        setAllCarriers(carriers);
        if (EditedData?.carriers) {
          setSelectedCarrierIds(
            new Set(EditedData.carriers.filter(c => c.active).map(c => c.id))
          );
        }
      })
      .catch(() => ErrorToast('Failed to load carriers'))
      .finally(() => setCarriersLoader(false));
  }, [EditedData]);

  // Get initials from name (first letter of each word)
  const getInitials = name => {
    if (!name) return '';
    const words = name.trim().split(/\s+/);
    if (words.length === 1) {
      // If single word, take first two letters or just first letter
      return words[0].substring(0, 2).toUpperCase();
    }
    // If multiple words, take first letter of first two words
    return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
  };

  const adminInitials = getInitials(`${firstName} ${lastName}`) || 'A';
  const companyInitials = getInitials(companyName);

  return (
    <Box sx={{ minHeight: '90vh' }}>
      <ToastContainer position='top-center' autoClose={3000} />

      {/* Header */}
      <Box
        sx={{
          bgcolor: 'white',
          borderBottom: '1px solid #e0e0e0',
          px: 3,
          py: 1.5
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            maxWidth: 1400,
            mx: 'auto'
          }}
        >
          <Typography variant='h6' fontWeight='600'>
            {EditedData ? 'Edit Agency' : 'Add New Agency'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant='outlined'
              onClick={() => history.push('/manage-agencies')}
              startIcon={<ArrowBackIcon />}
              size='small'
            >
              View Agencies
            </Button>
            <Button
              color='success'
              variant='contained'
              onClick={handleSubmit}
              disabled={createAgencyLoader}
              startIcon={
                createAgencyLoader ? (
                  <CircularProgress size={18} />
                ) : (
                  <SaveIcon />
                )
              }
              size='small'
            >
              {EditedData?.company ? 'Update Agency' : 'Create Agency'}
            </Button>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          p: 2,
          maxWidth: 1400,
          mx: 'auto'
        }}
      >
        <Grid container spacing={2}>
          {/* Left Column - Agency & Admin Info */}
          <Grid item xs={12} md={6}>
            <Paper
              sx={{
                p: 2,
                borderRadius: 2,
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              {/* Agency Admin Info Section - with avatar and inputs */}
              <Box sx={{ mb: 2.5 }}>
                <Typography
                  variant='subtitle1'
                  fontWeight='600'
                  sx={{ mb: 1.5 }}
                >
                  Admin Agency Info*
                </Typography>

                <Grid container spacing={2} alignItems='center'>
                  <Grid
                    item
                    xs={12}
                    sm={2}
                    sx={{ display: 'flex', justifyContent: 'center' }}
                  >
                    <Avatar
                      sx={{
                        width: 86,
                        height: 86,
                        bgcolor: '#1976d2',
                        fontSize: 24
                      }}
                    >
                      {adminInitials}
                    </Avatar>
                  </Grid>
                  <Grid item xs={12} sm={10}>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          id='field-firstName'
                          size='small'
                          label='First Name'
                          value={firstName}
                          onChange={e =>
                            handleFieldChange('firstName', e.target.value)
                          }
                          onBlur={() =>
                            handleFieldBlur(
                              'firstName',
                              firstName,
                              validateFirstName
                            )
                          }
                          error={!!fieldError.firstName}
                          helperText={fieldError.firstName}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          id='field-lastName'
                          size='small'
                          label='Last Name'
                          value={lastName}
                          onChange={e =>
                            handleFieldChange('lastName', e.target.value)
                          }
                          onBlur={() =>
                            handleFieldBlur(
                              'lastName',
                              lastName,
                              validateLastName
                            )
                          }
                          error={!!fieldError.lastName}
                          helperText={fieldError.lastName}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          id='field-email'
                          size='small'
                          label='Email Address'
                          type='email'
                          value={email}
                          onChange={e =>
                            handleFieldChange('email', e.target.value)
                          }
                          onBlur={() =>
                            handleFieldBlur('email', email, validateEmail)
                          }
                          error={!!fieldError.email}
                          helperText={fieldError.email}
                          fullWidth
                          disabled={!!EditedData?.adminName}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          id='field-phone'
                          size='small'
                          label='Phone Number'
                          value={phoneNumber}
                          onChange={handlePhoneChange}
                          onBlur={handlePhoneBlur}
                          error={!!fieldError.phone}
                          helperText={fieldError.phone}
                          fullWidth
                          placeholder='(123) 456-7890'
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Agency Info Section */}
              <Box sx={{ mb: 2.5 }}>
                <Typography
                  variant='subtitle1'
                  fontWeight='600'
                  sx={{ mb: 1.5 }}
                >
                  Agency Info*
                </Typography>

                <Grid container spacing={2} alignItems='center'>
                  <Grid
                    item
                    xs={12}
                    sm={2}
                    sx={{ display: 'flex', justifyContent: 'center' }}
                  >
                    <input
                      type='file'
                      accept='image/*'
                      onChange={handleImageUpload}
                      style={{ display: 'none' }}
                      id='agency-logo-upload'
                    />
                    <label htmlFor='agency-logo-upload'>
                      {agencyImage ? (
                        <Avatar
                          src={agencyImage}
                          sx={{
                            width: 86,
                            height: 86,
                            cursor: 'pointer',
                            borderRadius: 1.5
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: 86,
                            height: 86,
                            borderRadius: 1.5,
                            bgcolor: '#1976d2',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            border: '1px dashed #ccc'
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 28,
                              fontWeight: 'bold',
                              color: 'white'
                            }}
                          >
                            {companyInitials}
                          </Typography>
                        </Box>
                      )}
                    </label>
                  </Grid>
                  <Grid item xs={12} sm={10}>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12}>
                        <TextField
                          id='field-companyName'
                          size='small'
                          label='Enter Agency name'
                          value={companyName}
                          onChange={e =>
                            handleFieldChange('companyName', e.target.value)
                          }
                          onBlur={() =>
                            handleFieldBlur(
                              'companyName',
                              companyName,
                              validateCompanyName
                            )
                          }
                          error={!!fieldError.companyName}
                          helperText={fieldError.companyName}
                          fullWidth
                          disabled={!!EditedData?.company}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl component='fieldset'>
                          <RadioGroup
                            row
                            value={agencyStatus}
                            onChange={e => setAgencyStatus(e.target.value)}
                            sx={{ gap: 2 }}
                          >
                            <FormControlLabel
                              value='Active'
                              control={<Radio size='small' />}
                              label='Active'
                            />
                            <FormControlLabel
                              value='Inactive'
                              control={<Radio size='small' />}
                              label='Inactive'
                            />
                          </RadioGroup>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Agency Address Section */}
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant='subtitle1'
                  fontWeight='600'
                  sx={{ mb: 1.5 }}
                >
                  Agency Address*
                </Typography>

                <AddressAutocomplete
                  inputVariant='outlined'
                  label='Address'
                  defaultValue={address.address || ''}
                  onInputChange={val => {
                    if (fieldError.address) {
                      setFieldError(prev => ({ ...prev, address: '' }));
                    }
                  }}
                  onSelectDetails={details => {
                    if (details) {
                      const fullAddress = [
                        details?.company &&
                        details?.company?.toLowerCase() !==
                          details?.streetLines?.toLowerCase()
                          ? details.company
                          : null,
                        details?.streetLines,
                        details?.city,
                        details?.state,
                        details?.zip,
                        details?.country
                      ]
                        .filter(Boolean)
                        .join(', ');

                      const newAddress = {
                        City: details.city || '',
                        State: details.state || '',
                        ZipCode: details.zip || '',
                        CountryCode: details.country || '',
                        address: fullAddress,
                        isManualEntry: details.isUserTyped || false
                      };
                      setAddress(newAddress);
                      addressRef.current = newAddress;
                      if (fieldError.address) {
                        setFieldError(prev => ({ ...prev, address: '' }));
                      }
                    } else {
                      const emptyAddress = {
                        City: '',
                        State: '',
                        ZipCode: '',
                        CountryCode: '',
                        address: '',
                        isManualEntry: false
                      };
                      setAddress(emptyAddress);
                      addressRef.current = emptyAddress;
                    }
                  }}
                />

                {fieldError.address && (
                  <Typography
                    variant='caption'
                    color='error'
                    sx={{ mt: 0.5, ml: 1.5, display: 'block' }}
                  >
                    {fieldError.address}
                  </Typography>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Right Column - Carrier Selection */}
          <Grid item xs={12} md={6}>
            <Paper
              sx={{
                p: 2,
                borderRadius: 2,
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <Typography variant='subtitle1' fontWeight='600' sx={{ mb: 1.5 }}>
                Carrier Selection
              </Typography>

              {/* Tabs like buttons */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 1,
                  mb: 1.5,
                  borderBottom: 1,
                  borderColor: 'divider'
                }}
              >
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    onClick={() => setCarrierTab('All')}
                    size='small'
                    sx={{
                      textTransform: 'none',
                      fontWeight: carrierTab === 'All' ? 600 : 400,
                      color:
                        carrierTab === 'All'
                          ? 'primary.main'
                          : 'text.secondary',
                      borderBottom: carrierTab === 'All' ? 2 : 0,
                      borderColor: 'primary.main',
                      borderRadius: 0,
                      pb: 0.5,
                      minWidth: 'auto',
                      px: 1
                    }}
                  >
                    All
                  </Button>
                  <Button
                    onClick={() => setCarrierTab('LTL')}
                    size='small'
                    sx={{
                      textTransform: 'none',
                      fontWeight: carrierTab === 'LTL' ? 600 : 400,
                      color:
                        carrierTab === 'LTL'
                          ? 'primary.main'
                          : 'text.secondary',
                      borderBottom: carrierTab === 'LTL' ? 2 : 0,
                      borderColor: 'primary.main',
                      borderRadius: 0,
                      pb: 0.5,
                      minWidth: 'auto',
                      px: 1
                    }}
                  >
                    LTL
                  </Button>
                  <Button
                    onClick={() => setCarrierTab('Parcel')}
                    size='small'
                    sx={{
                      textTransform: 'none',
                      fontWeight: carrierTab === 'Parcel' ? 600 : 400,
                      color:
                        carrierTab === 'Parcel'
                          ? 'primary.main'
                          : 'text.secondary',
                      borderBottom: carrierTab === 'Parcel' ? 2 : 0,
                      borderColor: 'primary.main',
                      borderRadius: 0,
                      pb: 0.5,
                      minWidth: 'auto',
                      px: 1
                    }}
                  >
                    Parcel
                  </Button>
                </Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={allSelected}
                      onChange={handleSelectAll}
                      size='small'
                    />
                  }
                  label='Select All'
                  sx={{
                    '& .MuiFormControlLabel-label': { fontSize: 12 },
                    mr: 0
                  }}
                />
              </Box>

              <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                {carriersLoader ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                      minHeight: 200
                    }}
                  >
                    <CircularProgress size={32} />
                  </Box>
                ) : filteredCarriers.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant='body2' color='text.secondary'>
                      No carriers found
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns:
                        'repeat(auto-fill, minmax(100px, 1fr))',
                      gap: 1,
                      p: 0.5
                    }}
                  >
                    {filteredCarriers.map(carrier => (
                      <CarrierCard
                        key={carrier.id}
                        carrier={carrier}
                        isSelected={selectedCarrierIds.has(carrier.id)}
                        onToggle={handleCarrierToggle}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Image Crop Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Adjust Image</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              position: 'relative',
              height: 300,
              bgcolor: '#333',
              borderRadius: 1,
              overflow: 'hidden'
            }}
          >
            <Cropper
              image={tempImage}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={(_, pixels) => setCroppedAreaPixels(pixels)}
            />
          </Box>
          <Slider
            value={zoom}
            min={1}
            max={3}
            step={0.1}
            onChange={(_, v) => setZoom(v)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCropConfirm} variant='contained'>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
